import { Typography } from "@mui/material";
import { useStyles } from "../RCACompliance.styles";
import { useTranslation } from "react-i18next";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useContext, useState, useEffect, useMemo } from "react";
import { RCAAPComplianceEntity } from "entities/RCA/RCAAPComplianceEntity/RCAAPComplianceEntity";
import { RCAAPComplianceContext } from "contexts/RCAAPComplianceContext/RCAAPComplianceContext";
import moment from "moment";
import RCAWithNoMethodologyUsedModal from "./RCAWithNoMethodologyUsedModal";
import { IRcaMethodologyUsedByUser } from "entities/RCA/RCAAPComplianceEntity/RCAAPComplianceEntityInterfaces";

const RCAMethodologyUsedByUser = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { filters } = useContext(RCAAPComplianceContext);
    const [summaryData, setSummaryData] = useState({
        fiveWhys: 0,
        fishbone: 0,
    });
    const [userSelected, setUserSelected] = useState<IRcaMethodologyUsedByUser>();
    const [openRCAWithNoMethodologyUsedModal, setOpenRCAWithNoMethodologyUsedModal] = useState<boolean>(false);
    const argsFilters = useMemo(
        () => ({
            date: moment().subtract(Number(filters.periodOption), "day").startOf("day").toISOString(),
            meetingTypeIds: filters.meetingTypeIds,
            totalMeetingTypes: filters.totalMeetingTypes,
        }),
        [filters],
    );
    const { rcaMethodologyUsedByUser } = RCAAPComplianceEntity.GetRcaMethodologyUsedByUser(argsFilters);

    useEffect(() => {
        if (rcaMethodologyUsedByUser) {
            const fiveWhys =
                rcaMethodologyUsedByUser.reduce((acc, user) => acc + user.five_why_percentage, 0) /
                rcaMethodologyUsedByUser.length;
            const fishbone =
                rcaMethodologyUsedByUser.reduce((acc, user) => acc + user.fishbone_percentage, 0) /
                rcaMethodologyUsedByUser.length;

            setSummaryData({
                fiveWhys,
                fishbone,
            });
        }
    }, [rcaMethodologyUsedByUser]);

    return (
        <>
            <div className={classes.card}>
                <div className={classes.tableHeader}>
                    <Typography variant="rcaTitle" className={classes.cardTitle}>
                        {t("rca.compliance.rca_compliance.rca_methodology_used.title")}
                    </Typography>
                </div>
                <table className={classes.table}>
                    {rcaMethodologyUsedByUser.length > 0 ? (
                        <>
                            <thead>
                                <tr>
                                    <th className={classes.th}>
                                        {t("rca.compliance.rca_compliance.rca_methodology_used.user")}
                                    </th>
                                    <th className={classes.th}>
                                        {t("rca.compliance.rca_compliance.rca_methodology_used.fiveWHYS")}
                                    </th>
                                    <th className={classes.th}>
                                        {t("rca.compliance.rca_compliance.rca_methodology_used.fishbone")}
                                    </th>
                                    <th className={classes.th}>
                                        {t("rca.compliance.rca_compliance.rca_methodology_used.none")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rcaMethodologyUsedByUser.map((user) => (
                                    <tr key={user.user_id}>
                                        <td className={classes.thTd}>
                                            <div className={classes.userNameContainer}>
                                                {user.media_url ? (
                                                    <img
                                                        src={user.media_url}
                                                        alt={`${user.first_name} ${user.last_name}`}
                                                        className={classes.userImage}
                                                    />
                                                ) : (
                                                    <AccountCircleIcon className={classes.userImage} />
                                                )}
                                                {user.first_name} {user.last_name}
                                            </div>
                                        </td>
                                        <td className={classes.thTd}>{user.five_why_percentage}%</td>
                                        <td className={classes.thTd}>{user.fishbone_percentage}%</td>
                                        <td
                                            onClick={() => {
                                                setUserSelected(user);
                                                setOpenRCAWithNoMethodologyUsedModal(true);
                                            }}
                                            className={classes.thTdHighlighted}
                                        >
                                            {user.none_percentage}%
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className={classes.tfootTd}>
                                        {t("rca.compliance.rca_compliance.rca_methodology_used.total")}
                                    </td>
                                    <td className={classes.tfootTd}>{summaryData.fiveWhys}%</td>
                                    <td className={classes.tfootTd}>{summaryData.fishbone}%</td>
                                    <td className={classes.tfootTd}></td>
                                </tr>
                            </tfoot>
                        </>
                    ) : (
                        <p className={classes.noData}>
                            {t("rca.compliance.rca_compliance.rca_methodology_used.noDataFound")}
                        </p>
                    )}
                </table>
            </div>
            <RCAWithNoMethodologyUsedModal
                open={openRCAWithNoMethodologyUsedModal}
                handleClose={() => setOpenRCAWithNoMethodologyUsedModal(false)}
                data={userSelected}
            />
        </>
    );
};

export default RCAMethodologyUsedByUser;
